import { IconButtonProps, PaperProps, PopperProps } from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { ElementType, FC, forwardRef, ReactElement, ReactNode, Ref, useState } from 'react';
import MuiAutocomplete, {
  autocompleteClasses,
  AutocompleteProps as MuiAutocompleteProps,
  AutocompletePropsSizeOverrides,
} from '@mui/material/Autocomplete';
import { ChipTypeMap } from '@mui/material/Chip';
import Popper from '@mui/material/Popper';
import { styled, Theme } from '@mui/material/styles';
import { OverridableStringUnion } from '@mui/types';
import InputAdornment from '../InputAdornment';
import { InputBaseProps } from '../InputBase';
import TextField, { TextFieldProps } from '../TextField2';
import palette from '../theme/palette';
import AutocompleteListBoxComponent from './AutocompleteListBoxComponent';
import CrossFill from '@o2/icons/CrossFill';
import ChevronDown from '@o2/icons/ChevronDown';
import { INPUT_BORDER_RADIUS } from '../theme/constants';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import colors from '../theme/colors';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';
import { ModifierPhases } from '@popperjs/core/lib/enums';
import { ModifierArguments } from '@popperjs/core/lib/types';
import CircularProgress from '../CircularProgress';

declare module '@mui/material/Autocomplete' {
  interface AutocompletePropsSizeOverrides {
    large: true;
  }
}

export interface AutocompleteProps<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent']
> extends Omit<
    MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>,
    'renderInput' | 'slotProps'
  > {
  renderInput?: (params: TextFieldProps) => ReactNode;
  ListBoxComponent?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>['ListboxComponent'];
  PopperComponent?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>['PopperComponent'];
  disableListWrap?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>['disableListWrap'];
  defaultValue?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>['defaultValue'];

  /**
   * Value of the input.
   */
  value?: string | any;

  /**
   * Texts from TextField text elements
   */
  label?: ReactNode;

  labelNote?: ReactNode;

  helperText?: ReactNode;

  errorText?: ReactNode;

  /**
   * Color of the input.
   */
  color?: 'primary' | 'secondary' | 'internal' | 'success' | 'error' | 'warning';

  /**
   * Same as Material UI variant. We use "outlined" only for now. This can change in the future, though.
   */
  variant?: 'outlined' | 'filled' | 'standard';

  minWidth?: string;
  error?: boolean;

  /**
   * Function to extract the displayed text from an option.
   * BEWARE: if using a custom option type and it doesn't have a `label` property, you MUST supply this function so
   * that your options do not show up as empty strings!
   */
  getOptionLabel?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['getOptionLabel'];

  /**
   * Virtualized autocomplete list (for > 10 items in list)
   */
  virtualized?: boolean;

  /**
   * Optional content for the start adornment of the text input field.
   * Unlike in MUI, wrapping this content in `<InputAdornment />` is handled internally, so DO NOT do it manually!
   * If a custom `renderInput` method is provided, the adornment is passed as part of its `params` argument.
   */
  startAdornment?: ReactNode;

  /**
   * Optional content for the end adornment of the text input field.
   * Unlike in MUI, wrapping this content in `<InputAdornment />` is handled internally, so DO NOT do it manually!
   * If a custom `renderInput` method is provided, the adornment is passed as part of its `params` argument.
   *
   * If loading happens, automatic loader is displayed in endAdornment. EndAdornment can be overridden by passed prop so keep
   * in mind to provide loading stat if applicable.
   */
  endAdornment?: ReactNode;

  isLoading?: boolean;

  size?: OverridableStringUnion<NonNullable<InputBaseProps['size']>, AutocompletePropsSizeOverrides>;

  /**
   * When placement of Popper is anywhere else than on bottom.
   */
  detachedPopper?: boolean;

  slotProps?: {
    clearIndicator?: Partial<IconButtonProps>;
    paper?: PaperProps;
    popper?: Partial<PopperProps>;
    popupIndicator?: Partial<IconButtonProps>;
    input?: Partial<TextFieldProps>;
  };
}

export interface AutocompleteOption {
  label: string;
}

export interface AutocompleteOptionWithValue extends AutocompleteOption {
  value: number | string;
}

const StyledMuiAutocomplete = styled(MuiAutocomplete, {
  name: 'O2Autocomplete',
  slot: 'root',
  shouldForwardProp: (prop) =>
    sysShouldForwardProp(prop) &&
    prop !== 'minWidth' &&
    prop !== 'virtualized' &&
    prop !== 'detachedPopper' &&
    prop !== 'startAdornment' &&
    prop !== 'endAdornment' &&
    prop !== 'isLoading',
})<AutocompleteProps<string | { label: string } | { label: string; value: number | string }>>(
  ({ theme, minWidth, detachedPopper, startAdornment, endAdornment }) => {
    return {
      minWidth,
      /**
       * When autocomplete popper displays on top of the input, change border radius on all input corners.
       * (double `&&` for higher specificity to override styleOverrides)
       */
      ...(detachedPopper === true && {
        [`&& .${outlinedInputClasses.root} > .${outlinedInputClasses.notchedOutline}`]: {
          borderBottomLeftRadius: INPUT_BORDER_RADIUS,
          borderBottomRightRadius: INPUT_BORDER_RADIUS,
          borderWidth: '1px',
          borderBottomStyle: 'solid',
        },
        [`&& .${outlinedInputClasses.root}`]: {
          borderBottomLeftRadius: INPUT_BORDER_RADIUS,
          borderBottomRightRadius: INPUT_BORDER_RADIUS,
        },
      }),
      '.MuiAutocomplete-input.MuiAutocomplete-input': {
        padding: 0,
        paddingBottom: 2,
        ...(startAdornment && {
          paddingLeft: (theme as Theme).spacing(1),
        }),
        ...(endAdornment && {
          paddingRight: (theme as Theme).spacing(1),
        }),
      },
    };
  }
) as FC<AutocompleteProps<any>>;

const O2Autocomplete = <T,>(props: AutocompleteProps<T>) => {
  /**
   * Default version used when the `getOptionLabel` prop is not provided.
   */
  const defaultOptionLabel = (option: T | AutocompleteOption | AutocompleteOptionWithValue) => {
    if (typeof option === 'string') {
      return option;
    }

    if (option !== null && typeof option === 'object' && 'label' in option) {
      return option.label;
    }

    throw Error('Values must be array of string or object with label prop.');
  };

  /**
   * Default version used when the `renderInput` prop is not provided.
   */
  const defaultRenderInput = (params: TextFieldProps) => {
    return (
      <TextField
        {...params}
        label={label}
        labelNote={labelNote}
        helperText={helperText}
        errorText={errorText}
        color={color}
        variant={variant}
        error={error}
      />
    );
  };

  const {
    label,
    labelNote,
    helperText,
    errorText,
    color,
    variant,
    error = false,
    virtualized = false,
    getOptionLabel = defaultOptionLabel,
    minWidth,
    noOptionsText = 'o2-ui.noOptionsText',
    loadingText = 'o2-ui.loadingText',
    startAdornment,
    endAdornment = props.endAdornment || (props.isLoading ? <CircularProgress color="inherit" size={20} /> : undefined),
    renderInput = defaultRenderInput,
    slotProps,
    ...otherProps
  } = props;

  /**
   * Add adornment and size props to the text field render function.
   *
   * Adding size manually is required because MUI force-sets fullWidth and only supports 'small', not 'large' like we do:
   * https://github.com/mui/material-ui/blob/v5.10.7/packages/mui-material/src/Autocomplete/Autocomplete.js#L545
   */
  const renderInputWithAdornmentsAndSize = (params: TextFieldProps) => {
    return renderInput({
      ...slotProps?.input,
      ...params,
      fullWidth: props.fullWidth,
      size: props.size,
      sx: {
        ...slotProps?.input?.sx,
      },

      InputProps: {
        ...params.InputProps,
        startAdornment: startAdornment ? (
          <>
            <InputAdornment position="start">{startAdornment}</InputAdornment>
            {params.InputProps?.startAdornment}
          </>
        ) : (
          params.InputProps?.startAdornment
        ),
        endAdornment: endAdornment ? (
          <>
            <InputAdornment position="end">{endAdornment}</InputAdornment>
            {params.InputProps?.endAdornment}
          </>
        ) : (
          params.InputProps?.endAdornment
        ),
      },
    });
  };

  const getOptionValue = (option: T | AutocompleteOption | AutocompleteOptionWithValue, label: string) => {
    if (option !== null && typeof option === 'object' && 'value' in option) {
      return option.value;
    }

    return label;
  };

  const renderOption: AutocompleteProps<T>['renderOption'] = (props, option, { inputValue }) => {
    const label = getOptionLabel(option);
    const matches = match(label, inputValue, {
      insideWords: true,
    });
    const parts = parse(label, matches);

    const content = (
      <div>
        {parts.map((part, index) => (
          <span
            key={index}
            style={{
              fontWeight: part.highlight ? 600 : 400,
              color: part.highlight ? (color ? palette[color].main : palette.text.primary) : undefined,
            }}
          >
            {part.text}
          </span>
        ))}
      </div>
    );

    if (virtualized) {
      return [props, content];
    }

    return (
      <li data-value={getOptionValue(option, label)} {...props}>
        {content}
      </li>
    );
  };

  const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
      boxSizing: 'border-box',
      '& ul': {
        padding: 0,
        margin: 0,
      },
    },
  });

  const detachedPopperModifier = [
    {
      name: 'detachedPopper',
      enabled: true,
      phase: 'main' as ModifierPhases,
      fn({ state }: ModifierArguments<object>) {
        if (state.placement === 'bottom') {
          setDetachedPopper(false);
        } else {
          setDetachedPopper(true);
        }
      },
    },
  ];

  const modifiers = slotProps?.popper?.modifiers
    ? [...detachedPopperModifier, ...slotProps.popper.modifiers]
    : [...detachedPopperModifier];

  const [detachedPopper, setDetachedPopper] = useState(false);

  return (
    <StyledMuiAutocomplete
      classes={{ paper: props.className }}
      ListboxProps={
        !virtualized
          ? {
              style: {
                padding: 0,
              },
            }
          : undefined
      }
      disableListWrap={virtualized}
      PopperComponent={virtualized ? StyledPopper : undefined}
      ListboxComponent={virtualized ? AutocompleteListBoxComponent : undefined}
      minWidth={minWidth}
      renderInput={renderInputWithAdornmentsAndSize}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      noOptionsText={noOptionsText}
      slotProps={{
        ...slotProps,
        popper: {
          ...slotProps?.popper,
          className: slotProps?.popper?.className ?? props.className,
          modifiers: modifiers,
        },
        paper: {
          ...slotProps?.paper,
          sx: {
            ...slotProps?.paper?.sx,
            /**
             * Change styles in error state and if paper is on top of the input. Keep selector for higher specificity.
             */
            [`&.${autocompleteClasses.paper}`]: {
              borderColor: error ? palette.error.main : palette.primary.main,
              borderTopColor: colors.transparentGray[100],
              ...(detachedPopper && {
                borderTopLeftRadius: INPUT_BORDER_RADIUS,
                borderTopRightRadius: INPUT_BORDER_RADIUS,
                borderWidth: '1px',
                borderBottomStyle: 'solid',
                borderTopColor: error ? palette.error.main : palette.primary.main,
              }),
            },
          },
        },
      }}
      loadingText={loadingText}
      clearIcon={<CrossFill fontSize="small" />}
      popupIcon={<ChevronDown fontSize="small" />}
      color={color}
      detachedPopper={detachedPopper}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      {...otherProps}
    />
  );
};

type AutocompleteType = <
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
>(
  props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
    ref?: Ref<HTMLDivElement>;
  }
) => ReactElement;

//@ts-expect-error Types of property 'getOptionLabel' are incompatible - can't be fixed straightforwardly
const Autocomplete = forwardRef((props, ref) => <O2Autocomplete ref={ref} {...props} />) as AutocompleteType;

//@ts-expect-error TS2339: Property 'displayName' does not exist on type 'AutocompleteType'.
Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;
